import {
  aggregateDealerSessionBalances,
  formatDealerBalanceLabel
} from './dealerSessionBalanceUtils';
import {
  aggregateSupplierSessionBalances
} from './supplierSessionBalanceUtils';
import { sortDealerSessionsByDate, sortSupplierSessionsByDate } from './sessionListSortUtils';

const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€' };

const ACTIVE_DEALER_STATUSES = new Set(['new_order', 'preparing', 'shipped']);

export const formatCompactMoney = (value, currency = 'TRY') => {
  const amount = parseFloat(value) || 0;
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return `${symbol}${(amount / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${symbol}${Math.round(amount / 1_000)}K`;
  return `${symbol}${amount.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
};

export const formatFullMoney = (value, currency = 'TRY') => {
  const amount = parseFloat(value) || 0;
  const code = currency === 'TRY' || currency === 'USD' || currency === 'EUR' ? currency : 'TRY';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const getSessionDateLabel = (session, sortFn) => {
  if (!session) return null;
  const dateStr = session.orderDate || session.sessionDate || null;
  const date = dateStr
    ? new Date(`${dateStr}T00:00:00`)
    : (session.createdAt?.toDate ? session.createdAt.toDate() : new Date(session.createdAt || Date.now()));
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const groupSessionsByDealerId = (sessions = []) =>
  sessions.reduce((acc, session) => {
    const dealerId = session?.dealerId;
    if (!dealerId) return acc;
    if (!acc[dealerId]) acc[dealerId] = [];
    acc[dealerId].push(session);
    return acc;
  }, {});

export const buildDealerSessionsSummary = (sessions = [], directPayments = []) => {
  const directList = Array.isArray(directPayments) ? directPayments : [];
  if (!sessions.length && !directList.length) {
    return {
      hasData: false,
      sessionCount: 0,
      activeCount: 0,
      grandTotal: 0,
      grandPaid: 0,
      openBalance: 0,
      creditBalance: 0,
      isCreditor: false,
      isBalanced: true,
      lastDateLabel: null,
      balanceLabel: 'Kayıt yok'
    };
  }

  const balances = aggregateDealerSessionBalances(sessions, directList);
  const latest = sortDealerSessionsByDate(sessions)[0];
  const activeCount = sessions.filter((session) =>
    ACTIVE_DEALER_STATUSES.has(session.orderStatus || 'new_order')
  ).length;
  const balanceInfo = formatDealerBalanceLabel(balances.netBalance);

  return {
    hasData: true,
    sessionCount: sessions.length,
    activeCount,
    grandTotal: balances.grandTotal,
    grandPaid: balances.grandPaid,
    openBalance: balances.openBalance,
    creditBalance: balances.creditBalance,
    isCreditor: balances.isCreditor,
    isBalanced: balances.isBalanced,
    lastDateLabel: getSessionDateLabel(latest),
    balanceType: balanceInfo.type,
    balanceAmount: balanceInfo.amount,
    balanceLabel: balanceInfo.label
  };
};

export const buildSupplierSessionsSummary = (sessions = [], exchangeRates = {}, supplierDirectPayments = []) => {
  const directList = Array.isArray(supplierDirectPayments) ? supplierDirectPayments : [];
  if (!sessions.length && !directList.length) {
    return {
      hasData: false,
      sessionCount: 0,
      currencyLines: [],
      totalRemainingTry: 0,
      totalCreditTry: 0,
      totalNetTry: 0,
      isCreditor: false,
      isBalanced: true,
      lastDateLabel: null
    };
  }

  const balances = aggregateSupplierSessionBalances(sessions, exchangeRates, directList);
  const latest = sortSupplierSessionsByDate(sessions)[0];

  return {
    hasData: true,
    sessionCount: sessions.length,
    currencyLines: balances.currencyLines.map((line) => ({
      currency: line.currency,
      sessionCount: line.sessionCount,
      total: line.total,
      sessionPaid: line.sessionPaid,
      directPaid: line.directPaid,
      paid: line.paid,
      remaining: line.openBalance,
      creditBalance: line.creditBalance,
      isCreditor: line.isCreditor,
      isBalanced: line.isBalanced
    })),
    totalRemainingTry: balances.totalDebtTry,
    totalCreditTry: balances.totalCreditTry,
    totalNetTry: balances.totalNetTry,
    isCreditor: balances.isCreditor,
    isBalanced: balances.isBalanced,
    lastDateLabel: getSessionDateLabel(latest, sortSupplierSessionsByDate)
  };
};

export const buildDealerRowSummary = (dealer, dealerSessions = [], supplierSessions = [], exchangeRates = {}) => {
  const firmType = dealer?.firmType || 'bayi';
  const directPayments = dealer?.directPayments || dealer?.paymentHistory || [];
  const supplierDirectPayments = dealer?.supplierDirectPayments || [];
  return {
    firmType,
    bayi: buildDealerSessionsSummary(dealerSessions, directPayments),
    supplier: buildSupplierSessionsSummary(supplierSessions, exchangeRates, supplierDirectPayments)
  };
};

export const buildAllDealerSummaries = (dealers = [], dealerSessions = [], supplierSessions = [], exchangeRates = {}) => {
  const bayiByDealer = groupSessionsByDealerId(dealerSessions);
  const supplierByDealer = groupSessionsByDealerId(supplierSessions);

  return dealers.reduce((acc, dealer) => {
    acc[dealer.id] = buildDealerRowSummary(
      dealer,
      bayiByDealer[dealer.id] || [],
      supplierByDealer[dealer.id] || [],
      exchangeRates
    );
    return acc;
  }, {});
};
